import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useToast } from '../../components/Toast';
import PaymentFlowStatus from '../../components/PaymentFlowStatus';
import './PaymentReceipt.css';

const PLAN_DETAILS = {
  pro_1month: { name: '왓퀴즈 PRO 1개월', period: '1개월' },
  pro_3month: { name: '왓퀴즈 PRO 3개월', period: '3개월' },
  pro_6month: { name: '왓퀴즈 PRO 6개월', period: '6개월' },
  pro_12month: { name: '왓퀴즈 PRO 12개월', period: '12개월' },
  pro_monthly: { name: '왓퀴즈 PRO 월간', period: '1개월' },
  monthly: { name: '왓퀴즈 PRO 월간', period: '1개월' },
  pro_yearly: { name: '왓퀴즈 PRO 연간', period: '12개월' },
  yearly: { name: '왓퀴즈 PRO 연간', period: '12개월' },
};

const PAYMENT_METHOD_NAMES = {
  toss_card: '신용·체크카드',
  toss_transfer: '계좌이체',
  toss_virtual_account: '가상계좌',
  toss_mobile_phone: '휴대폰',
  toss_easy_pay: '간편결제',
  toss_gift_certificate: '상품권',
  카드: '신용·체크카드',
  계좌이체: '계좌이체',
  휴대폰: '휴대폰',
  간편결제: '간편결제',
};

const getPaymentMethodName = (method) =>
  PAYMENT_METHOD_NAMES[method] || method || '결제수단 정보 없음';

const getPlanDetails = (record) => {
  const mapped = PLAN_DETAILS[record.plan_id];
  if (mapped) return mapped;

  return {
    name: record.description || '왓퀴즈 PRO 이용권',
    period: record.granted_days ? `${record.granted_days}일` : null,
  };
};

const PaymentReceipt = () => {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const [paymentData, setPaymentData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const receiptRef = useRef(null);

  // API 기본 URL 설정
  const getApiBaseUrl = () => {
    const currentUrl = window.location.hostname;
    if (currentUrl.includes('localhost')) {
      return 'http://localhost:8000';
    } else if (currentUrl.includes('whatquiztest')) {
      return 'https://quiz-api-test-397277209875.us-central1.run.app';
    } else {
      return 'https://quiz-api-397277209875.us-central1.run.app';
    }
  };

  // 결제 정보 조회
  useEffect(() => {
    let cancelled = false;

    const fetchPaymentData = async () => {
      try {
        const API_BASE_URL = getApiBaseUrl();
        const idToken = await currentUser.getIdToken(true);

        const response = await fetch(`${API_BASE_URL}/api/payment/status/${paymentId}`, {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (!cancelled) setPaymentData(result.data);
        } else if (!cancelled) {
          setError('결제 내역을 불러오지 못했어요.');
        }
      } catch (error) {
        console.error('결제 정보 조회 실패:', error);
        if (!cancelled) setError('결제 내역을 불러오지 못했어요.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    if (currentUser && paymentId) {
      fetchPaymentData();
    }

    return () => {
      cancelled = true;
    };
  }, [currentUser, paymentId, navigate]);

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 금액 포맷팅
  const formatAmount = (amount) => {
    return amount?.toLocaleString() || '0';
  };

  // PDF 다운로드
  const handleDownloadPDF = async () => {
    if (!receiptRef.current || isDownloading) return;

    setIsDownloading(true);
    try {
      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight) * 0.95; // 95%로 여백 확보
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10; // 상단 여백

      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      pdf.save(`왓퀴즈_영수증_${paymentId}.pdf`);
      showToast('영수증 PDF를 다운로드했어요', 'success', 6000);
    } catch (error) {
      console.error('PDF 생성 실패:', error);
      showToast('PDF를 만들지 못했어요. 잠시 후 다시 시도해주세요.', 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleOpenOfficialReceipt = () => {
    const receiptUrl = paymentData?.receipt?.url;
    if (!receiptUrl) return;

    if (receiptUrl.includes('ref=test')) {
      showToast('테스트 환경에서는 토스 공식 영수증이 제공되지 않아요', 'info');
      return;
    }

    const receiptWindow = window.open(receiptUrl, '_blank', 'noopener,noreferrer');
    if (receiptWindow) receiptWindow.opener = null;
  };

  if (isLoading) {
    return (
      <PaymentFlowStatus
        variant="processing"
        title="영수증을 불러오고 있어요"
        description="결제 내역을 안전하게 확인하고 있어요."
      />
    );
  }

  if (error || !paymentData?.payment_record) {
    return (
      <PaymentFlowStatus
        variant="error"
        title="영수증을 불러오지 못했어요"
        description="잠시 후 다시 시도하거나 구독 관리에서 결제 내역을 확인해주세요."
        detailCode={error}
        primaryAction={{
          label: '구독 관리로 돌아가기',
          onClick: () => navigate('/subscription'),
        }}
      />
    );
  }

  const paymentRecord = paymentData.payment_record;
  const amount = Number(paymentRecord.amount) || 0;
  const originalAmount = Number(paymentRecord.original_amount) || amount;
  const discountAmount = Number(paymentRecord.discount_amount)
    || Math.max(0, originalAmount - amount);
  const supplyAmount = Math.floor(amount / 1.1);
  const vatAmount = amount - supplyAmount;
  const planDetails = getPlanDetails(paymentRecord);
  const hasOfficialReceipt = Boolean(paymentData?.receipt?.url);

  return (
    <main className="receiptpage-page">
      <div className="receiptpage-container">
        <div className="receiptpage-toolbar">
          <button
            type="button"
            className="receiptpage-button receiptpage-button--ghost"
            onClick={() => navigate('/subscription')}
          >
            구독 관리
        </button>

          <div className="receiptpage-toolbar-actions">
            {hasOfficialReceipt && (
              <button
                type="button"
                className="receiptpage-button receiptpage-button--primary"
                onClick={handleOpenOfficialReceipt}
              >
                공식 영수증 열기
              </button>
            )}
            <button
              type="button"
              className="receiptpage-button receiptpage-button--secondary"
              disabled={isDownloading}
              onClick={handleDownloadPDF}
            >
              {isDownloading ? 'PDF 만드는 중...' : 'PDF로 저장하기'}
            </button>
          </div>
        </div>

        <article ref={receiptRef} className="receiptpage-document">
          <header className="receiptpage-header">
            <p className="receiptpage-brand">왓퀴즈</p>
            <h1 className="receiptpage-title">전자 영수증</h1>
            <p className="receiptpage-subtitle">결제한 내역을 확인할 수 있어요</p>
          </header>

          <div className="receiptpage-reference">
            <span className="receiptpage-reference-label">거래번호</span>
            <span className="receiptpage-reference-value">{paymentRecord.transaction_id}</span>
          </div>

          <section className="receiptpage-section">
            <h2 className="receiptpage-section-title">결제 정보</h2>
            <div className="receiptpage-row">
              <span className="receiptpage-label">결제일시</span>
              <span className="receiptpage-value">{formatDate(paymentRecord.timestamp)}</span>
            </div>
            <div className="receiptpage-row">
              <span className="receiptpage-label">결제수단</span>
              <span className="receiptpage-value">
                {getPaymentMethodName(paymentRecord.payment_method)}
              </span>
            </div>
            <div className="receiptpage-row">
              <span className="receiptpage-label">주문번호</span>
              <span className="receiptpage-value receiptpage-value--mono">
                {paymentRecord.order_no}
              </span>
            </div>
          </section>

          <section className="receiptpage-section">
            <h2 className="receiptpage-section-title">구매 내역</h2>
            <div className="receiptpage-product">
              <div>
                <p className="receiptpage-product-name">{planDetails.name}</p>
                {planDetails.period && (
                  <p className="receiptpage-product-detail">이용 기간 {planDetails.period}</p>
                )}
              </div>
              <strong className="receiptpage-product-price">{formatAmount(amount)}원</strong>
            </div>
          </section>

          <section className="receiptpage-total" aria-label="결제 금액 상세">
            {discountAmount > 0 && (
              <>
                <div className="receiptpage-row">
                  <span className="receiptpage-label">상품 금액</span>
                  <span className="receiptpage-value">{formatAmount(originalAmount)}원</span>
                </div>
                <div className="receiptpage-row">
                  <span className="receiptpage-label">할인 금액</span>
                  <span className="receiptpage-value receiptpage-discount">
                    -{formatAmount(discountAmount)}원
                  </span>
                </div>
              </>
            )}
            <div className="receiptpage-row">
              <span className="receiptpage-label">공급가액</span>
              <span className="receiptpage-value">{formatAmount(supplyAmount)}원</span>
            </div>
            <div className="receiptpage-row">
              <span className="receiptpage-label">부가세</span>
              <span className="receiptpage-value">{formatAmount(vatAmount)}원</span>
            </div>
            <div className="receiptpage-row receiptpage-total-final">
              <span className="receiptpage-label">총 결제 금액</span>
              <strong className="receiptpage-total-amount">{formatAmount(amount)}원</strong>
            </div>
          </section>

          <section className="receiptpage-section">
            <h2 className="receiptpage-section-title">구매자 정보</h2>
            <div className="receiptpage-row">
              <span className="receiptpage-label">이메일</span>
              <span className="receiptpage-value">{currentUser.email}</span>
            </div>
          </section>

          <section className="receiptpage-section">
            <h2 className="receiptpage-section-title">판매자 정보</h2>
            <div className="receiptpage-seller">
              <p><strong>상호명</strong> 플랙티컬 주식회사</p>
              <p><strong>대표자</strong> 강연구</p>
              <p><strong>사업자등록번호</strong> 853-81-03114</p>
              <p><strong>통신판매업신고번호</strong> 2024-충남천안-0144</p>
              <p><strong>대표전화</strong> 02-949-9158</p>
              <p><strong>이메일</strong> angel@plactical.com</p>
              <p className="receiptpage-seller-address">
                <strong>주소</strong> 서울특별시 노원구 공릉로 232,
                제1창업보육센터 404호 (공릉동, 서울과학기술대학교)
              </p>
            </div>
          </section>

          <footer className="receiptpage-document-footer">
            이 영수증은 전자상거래 등에서의 소비자보호에 관한 법률에 따라 발행했어요.
            <br />
            문의: angel@plactical.com
          </footer>
        </article>
      </div>
    </main>
  );
};

export default PaymentReceipt; 